import { AdUnit } from '@pubstack/common/src/adunit'
import { NativeTemplate } from '@pubstack/common/src/nativeTemplate'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAdUnits, useNative } from '~/api/adm-api.hook'
import { useSitesStacksByAdUnits } from '~/api/adm/ad-units.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useToast } from '~/components/Toast/useToasts'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PureAdmAdUnitEditPage } from './PureAdmAdUnitEditPage'

const emptyAdunit: AdUnit = {
  id: '',
  name: '',
  divId: '',
  adServerAdUnitName: '',
  scopeId: '',
  enabled: true,
  devices: [],
  sizes: [],
  customSizes: [],
  fluid: false,
  canOutstream: false,
  isInstream: false,
  dynamicEnabled: false,
  headerBiddingEnabled: true,
  slotKeyValues: [],
  containerMaxSize: {
    height: undefined,
    width: undefined,
  },
}

export const AdmAdUnitEditPage = () => {
  const navigate = useNavigate()
  const [adUnit, setAdUnit] = useState<AdUnit>(emptyAdunit)
  const [templates, setTemplates] = useState<NativeTemplate[]>([])
  const { adUnitId } = useParams()
  const toast = useToast()
  const { byId, validateName } = useAdUnits(null)
  const native = useNative(null)
  const breadcrumbs = useBreadcrumbs(adUnit)
  const isSaving = useRef(false)
  const { refetch: refetchSitesStacks } = useSitesStacksByAdUnits()
  const user = useAuthenticatedUser()
  const hasInstreamFeature = user.hasFeature('instream')
  const hasInterstitialFeature = user.hasFeature('interstitial')

  async function loadAdUnit(adUnitId?: string) {
    if (adUnitId) {
      try {
        const adUnitToUpdate = await byId.get(adUnitId)
        setAdUnit({ ...adUnitToUpdate, containerMaxSize: adUnitToUpdate.containerMaxSize ?? { width: undefined, height: undefined } })
      } catch (exception) {
        toast.alert('An error occurred while fetching this adUnit.')
        throw exception
      }
    }
  }

  const loadTemplates = async () => {
    try {
      const nativeTemplates = await native.template.all()
      setTemplates(nativeTemplates)
    } catch (exception) {
      toast.alert('An error occured while fetching native templates.')
      throw exception
    }
  }

  async function createAdUnit(adUnit: AdUnit) {
    if (isSaving.current) return
    isSaving.current = true
    try {
      const newAdUnit: AdUnit = await byId.post(adUnit)
      setAdUnit(newAdUnit)
      toast.success(
        <>
          <strong>{newAdUnit.name}</strong> added to your inventory.`
        </>
      )
      isSaving.current = false
      navigate('/monetization/one/adunits')
    } catch (exception) {
      toast.alert('An error occurred while saving this adUnit.')
      isSaving.current = false
      throw exception
    }
  }

  async function updateAdUnit(adUnit: AdUnit) {
    if (isSaving.current) return
    isSaving.current = true
    try {
      const updatedAdUnit: AdUnit = await byId.put(adUnit)
      setAdUnit(updatedAdUnit)
      const { data: result } = await refetchSitesStacks()
      toast.admSuccess(updatedAdUnit.name, !!result?.[updatedAdUnit.name], navigate)
      isSaving.current = false
      navigate('/monetization/one/adunits')
    } catch (exception) {
      toast.alert('An error occurred while saving this adUnit.')
      isSaving.current = false
      throw exception
    }
  }

  const validateAdUnitUniqueName = async (name?: string) => {
    return name ? validateName(name) : true
  }

  useEffect(() => {
    /* oxlint-disable react/set-state-in-effect -- legitimate data load (async fetch then setState) */
    loadTemplates()
    loadAdUnit(adUnitId)
    /* oxlint-enable react/set-state-in-effect */
  }, [adUnitId])

  // Reads the saving ref to pass the current value down (kept as a ref to avoid re-renders).
  // oxlint-disable-next-line react/refs
  const isSavingValue = isSaving.current

  return (
    <PureAdmAdUnitEditPage
      isLoading={byId.loading}
      isEditing={!!adUnit.id}
      adUnit={adUnit}
      breadcrumbs={breadcrumbs}
      onCreateAdUnit={createAdUnit}
      onUpdateAdUnit={updateAdUnit}
      onAdUnitsEditBackClick={() => navigate(-1)}
      validateUniqueName={validateAdUnitUniqueName}
      isSaving={isSavingValue}
      nativeTemplates={templates}
      hasInstreamFeature={hasInstreamFeature}
      hasInterstitialFeature={hasInterstitialFeature}
    />
  )
}

export default AdmAdUnitEditPage
